<!-- Layout Wrapper -->
<div class="flex flex-col h-screen w-full overflow-hidden font-sans">
  
  <!-- Header -->
  <header class="h-16 bg-white shadow-md flex items-center px-4 z-[1000] shrink-0 border-b border-gray-100">
    <div class="flex items-center w-full gap-4">
      <button class="p-2 hover:bg-black/5 rounded flex items-center justify-center transition-colors text-[#1976d2]" (click)="toggleSidebar()">
         <span class="material-symbols-outlined text-2xl">{{ collapsed() ? 'menu' : 'menu_open' }}</span>
       </button>
      
      <h1 class="m-0 text-xl font-semibold text-[#0c254d] truncate font-sans">
        {{ 'portal.header.title' | translate }}
      </h1>

      @if (pageTitle()) {
        <div class="absolute left-1/2 -translate-x-1/2 flex items-center gap-4 whitespace-nowrap hidden md:flex">
          <h2 class="m-0 text-2xl font-semibold text-[#1976d2] font-sans">
            {{ pageTitle()! | translate }}
          </h2>
          @if (pageCount() !== null) {
            <div class="inline-flex items-center justify-center bg-blue-50 text-blue-600 font-semibold text-[0.65rem] px-2.5 py-1 border border-blue-100 rounded-full shadow-sm hover:bg-blue-100 transition-colors uppercase tracking-wider h-6">
              <span class="material-symbols-outlined text-sm mr-1.5 opacity-70">book_4</span>
              <span class="leading-none">Total: {{ pageCount() }}</span>
            </div>
          }
        </div>
      }

      <div class="flex-1"></div>

      <!-- User Menu (CDK) -->
      <div class="flex items-center gap-2 mr-4">
        <button [cdkMenuTriggerFor]="userMenu" 
                class="text-gray-500 hover:text-[#1976d2] transition-colors p-1 flex items-center justify-center"
                title="User Menu">
          <span class="material-symbols-outlined text-3xl">account_circle</span>
        </button>

        <ng-template #userMenu>
          <div class="bg-white shadow-xl border border-gray-100 rounded-lg p-2 min-w-[220px] z-[1100] flex flex-col gap-1 font-sans" cdkMenu>
            <button class="flex items-center gap-3 px-4 py-3 hover:bg-blue-50/50 rounded-md transition-colors text-left w-full text-gray-700"
                    routerLink="/app/settings" cdkMenuItem>
              <span class="material-symbols-outlined text-gray-400">settings</span>
              <span>{{ 'portal.header.menu.settings' | translate }}</span>
            </button>
            <hr class="my-1 border-gray-100">
            <button class="flex items-center gap-3 px-4 py-3 hover:bg-red-50/50 rounded-md transition-colors text-red-600 text-left w-full"
                    (click)="logout()" cdkMenuItem>
              <span class="material-symbols-outlined text-red-400">logout</span>
              <span>{{ 'portal.header.menu.logout' | translate }}</span>
            </button>
          </div>
        </ng-template>
      </div>
    </div>
  </header>

  <!-- Sidebar + Main Content Container -->
  <div class="flex-1 flex overflow-hidden relative min-h-0 bg-[#f5f5f5]">
    
      <!-- Sidebar Navigation -->
      <aside mwlResizable
             [enableGhostResize]="!isMobile().matches"
             (resizeEnd)="onResizeEnd($event)"
             [style.width]="sidebarWidthStyle()"
             class="bg-white border-r border-gray-200 shadow-sm transition-[width] duration-300 ease-in-out relative shrink-0 z-[90] flex flex-col"
             [class.md:relative]="!isMobile().matches"
             [class.fixed]="isMobile().matches"
             [class.inset-y-0]="isMobile().matches"
             [class.left-0]="isMobile().matches"
             [class.z-[2100]]="isMobile().matches"
             [class.shadow-2xl]="isMobile().matches && !collapsed()">
      
      <!-- Resize Handle -->
      <div mwlResizeHandle [resizeEdges]="{ right: true }" 
           class="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400/30 transition-colors z-[100] group">
        <div class="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-8 bg-white border border-gray-200 rounded-l-md shadow-sm items-center justify-center flex opacity-0 group-hover:opacity-100 transition-opacity">
          <span class="text-[10px] text-gray-400">⋮⋮</span>
        </div>
      </div>

      <nav class="p-2 flex flex-col gap-1 overflow-y-auto no-scrollbar">
        
        <!-- Dashboard -->
        <a routerLink="/app/dashboard" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm" 
           [routerLinkActiveOptions]="{ exact: true }"
           class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
           [title]="collapsed() ? ('portal.nav.dashboard' | translate) : ''">
          <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">dashboard</span>
          <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
            {{ 'portal.nav.dashboard' | translate }}
          </span>
        </a>

        <!-- Meine Daten -->
        <a routerLink="/app/my-data" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
           class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
           [title]="collapsed() ? ('portal.nav.mydata' | translate) : ''">
          <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">person</span>
          <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
            {{ 'portal.nav.mydata' | translate }}
          </span>
        </a>

        <!-- Student Items -->
        @if (roleService.isStudent()) {
          <a routerLink="/app/degrees" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
             class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
             [title]="collapsed() ? ('contacts.degrees.title' | translate) : ''">
            <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">school</span>
            <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
              {{ 'contacts.degrees.title' | translate }}
            </span>
          </a>

          <a routerLink="/app/modules" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
             class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
             [title]="collapsed() ? ('portal.degrees.modules.title' | translate) : ''">
            <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">view_module</span>
            <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
              {{ 'portal.degrees.modules.title' | translate }}
            </span>
          </a>

          <a routerLink="/app/lectures" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
             class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
             [title]="collapsed() ? ('portal.nav.lectures' | translate) : ''">
            <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">calendar_month</span>
            <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
              {{ 'portal.nav.lectures' | translate }}
            </span>
          </a>

          <a routerLink="/app/rechnungen" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
             class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
             [title]="collapsed() ? ('generic.invoices' | translate) : ''">
            <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">receipt_long</span>
            <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
              {{ 'generic.invoices' | translate }}
            </span>
          </a>

          <a routerLink="/app/unterlagen" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
             class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
             [title]="collapsed() ? ('portal.nav.documents' | translate) : ''">
            <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">description</span>
            <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
              {{ 'portal.nav.documents' | translate }}
            </span>
          </a>
        }

        <!-- Lecturer Items -->
        @if (roleService.isLecturer()) {
          <div class="flex flex-col">
             <button (click)="collapsed() ? null : toggleEinsatzplanung($event)"
                     [cdkMenuTriggerFor]="collapsed() ? schedulingMenu : null"
                     class="flex items-center justify-between gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group w-full overflow-hidden text-[1.154rem]"
                     [title]="collapsed() ? ('portal.nav.scheduling' | translate) : ''">
               <div class="flex items-center gap-4">
                 <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">calendar_month</span>
                 <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
                   {{ 'portal.nav.scheduling' | translate }}
                 </span>
               </div>
               @if (!collapsed()) {
                 <span class="material-symbols-outlined text-xl transition-transform duration-300 transform"
                    [class.rotate-180]="einsatzplanungExpanded()">keyboard_arrow_down</span>
               }
             </button>
 
             <!-- Floating Menu for Collapsed State -->
             <ng-template #schedulingMenu>
               <div class="bg-white shadow-xl border border-gray-100 rounded-lg p-2 min-w-[200px] z-[2200] flex flex-col gap-1 font-sans ml-2" cdkMenu>
                 <div class="px-4 py-2 text-[0.7rem] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">
                   {{ 'portal.nav.scheduling' | translate }}
                 </div>
                 <a routerLink="/app/teachability" class="flex items-center gap-3 px-4 py-3 hover:bg-blue-50/50 rounded-md transition-colors text-gray-700 no-underline" cdkMenuItem>
                   <span class="material-symbols-outlined text-blue-600">school</span>
                   <span>{{ 'portal.nav.teachability' | translate }}</span>
                 </a>
                 <a routerLink="/app/availability" class="flex items-center gap-3 px-4 py-3 hover:bg-blue-50/50 rounded-md transition-colors text-gray-700 no-underline" cdkMenuItem>
                   <span class="material-symbols-outlined text-blue-600">event_available</span>
                   <span>{{ 'portal.nav.availability' | translate }}</span>
                 </a>
                 <a routerLink="/app/planning-overview" class="flex items-center gap-3 px-4 py-3 hover:bg-blue-50/50 rounded-md transition-colors text-gray-700 no-underline" cdkMenuItem>
                   <span class="material-symbols-outlined text-blue-600">assignment</span>
                   <span>{{ 'portal.nav.planning_overview' | translate }}</span>
                 </a>
               </div>
             </ng-template>

            <!-- Submenu Items -->
            @if (!collapsed() && einsatzplanungExpanded()) {
              <div class="flex flex-col gap-1 mt-1 ml-4 border-l-2 border-gray-100 pl-2">
                <a routerLink="/app/teachability" routerLinkActive="!text-blue-600 font-semibold !bg-blue-50/30 border-b-2 border-red-600"
                   class="flex items-center gap-3 p-3 rounded-md text-gray-600 hover:bg-blue-50/30 text-[1rem] transition-colors overflow-hidden">
                  <span class="material-symbols-outlined text-xl">school</span>
                  <span class="font-sans whitespace-nowrap">{{ 'portal.nav.teachability' | translate }}</span>
                </a>
                <a routerLink="/app/availability" routerLinkActive="!text-blue-600 font-semibold !bg-blue-50/30 border-b-2 border-red-600"
                   class="flex items-center gap-3 p-3 rounded-md text-gray-600 hover:bg-blue-50/30 text-[1rem] transition-colors overflow-hidden">
                  <span class="material-symbols-outlined text-xl">event_available</span>
                  <span class="font-sans whitespace-nowrap">{{ 'portal.nav.availability' | translate }}</span>
                </a>
                <a routerLink="/app/planning-overview" routerLinkActive="!text-blue-600 font-semibold !bg-blue-50/30 border-b-2 border-red-600"
                   class="flex items-center gap-3 p-3 rounded-md text-gray-600 hover:bg-blue-50/30 text-[1rem] transition-colors overflow-hidden">
                  <span class="material-symbols-outlined text-xl">assignment</span>
                  <span class="font-sans whitespace-nowrap">{{ 'portal.nav.planning_overview' | translate }}</span>
                </a>
              </div>
            }
          </div>
          
          <!-- Confirmed Plan -->
          <a routerLink="/app/confirmed-plan" routerLinkActive="!bg-blue-100/50 !text-blue-600 border-b-4 border-red-600 shadow-sm"
             class="flex items-center gap-4 p-3 rounded-lg text-gray-700 font-medium hover:bg-blue-50/50 transition-all group overflow-hidden text-[1.154rem]"
             [title]="collapsed() ? ('portal.nav.confirmed_plan' | translate) : ''">
            <span class="material-symbols-outlined shrink-0 text-2xl group-hover:scale-110 transition-transform">task_alt</span>
            <span [class]="'whitespace-nowrap transition-all duration-300 font-sans ' + (collapsed() ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0')">
              {{ 'portal.nav.confirmed_plan' | translate }}
            </span>
          </a>
        }
       </nav>
     </aside>
 
     <!-- Backdrop for Mobile Sidebar -->
     @if (isMobile().matches && !collapsed()) {
       <div (click)="toggleSidebar()" 
            class="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[2050] transition-opacity duration-300 animate-in fade-in cursor-pointer"></div>
     }

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto overflow-x-hidden shrink-0 min-w-0 flex flex-col bg-[#f5f5f5] custom-scrollbar" cdkScrollable>
      <div class="flex-1 flex flex-col min-h-0 relative">
        <router-outlet></router-outlet>
      </div>
    </main>
  </div>

  <!-- Footer -->
  <app-footer class="shrink-0"></app-footer>
</div>